import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Layers3, Loader2, Crown, Download, Lightbulb, AlertTriangle, Maximize2 } from "lucide-react";
import { api, formatApiError, mediaUrl } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { makeClipBody } from "@/components/editor/ExportPanel";
import { VariantsCompare, ScoreRing, TONE } from "@/components/editor/VariantsCompare";

const VariantCard = ({ c, best }) => {
  const { t } = useI18n();
  const color = TONE[c.variant] || "#a746ff";
  return (
    <div className="rounded-xl border bg-black/30 p-3 flex gap-3 cx-rise" style={{ borderColor: best ? color : "rgba(255,255,255,0.08)" }} data-testid={`variant-card-${c.variant}`}>
      <div className="w-20 shrink-0 aspect-[9/16] rounded-lg overflow-hidden bg-black grid place-items-center">
        {c.status === "done" ? <video src={mediaUrl(`/clips/${c.id}/file`)} muted loop playsInline controls={false} onMouseEnter={(e) => e.target.play()} onMouseLeave={(e) => e.target.pause()} onClick={(e) => (e.target.paused ? e.target.play() : e.target.pause())} className="w-full h-full object-cover cursor-pointer" data-testid={`variant-video-${c.variant}`} />
          : c.status === "error" ? <AlertTriangle className="w-5 h-5 text-[#ff4d6d]" /> : <Loader2 className="w-5 h-5 animate-spin" style={{ color }} />}
      </div>
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color }}>{t(`var.${c.variant}`)}</span>
              {best && <span className="flex items-center gap-1 text-[9px] font-semibold text-black rounded-full px-1.5 py-0.5" style={{ background: color }} data-testid="variant-best-badge"><Crown className="w-2.5 h-2.5" />{t("var.best")}</span>}
            </div>
            <div className="text-xs text-white line-clamp-2" data-testid={`variant-title-${c.variant}`}>{c.title}</div>
          </div>
          <ScoreRing score={c.viral?.score ?? 0} color={color} />
        </div>
        <div className="text-[10px] text-slate-500 flex items-center gap-1"><Lightbulb className="w-3 h-3" />{t("var.tips")}</div>
        <ul className="space-y-0.5" data-testid={`variant-tips-${c.variant}`}>
          {(c.viral?.tips || []).map((tip, i) => <li key={i} className="text-[10px] text-slate-300 leading-snug">• {tip}</li>)}
        </ul>
        <div className="text-[10px]">
          {c.status === "done" ? <a href={mediaUrl(`/clips/${c.id}/file`, "&download=1")} data-testid={`variant-download-${c.variant}`} className="inline-flex items-center gap-1 text-[#3ad7ff] hover:underline"><Download className="w-3 h-3" />{t("var.download")}</a>
            : <span className={c.status === "error" ? "text-[#ff4d6d]" : "text-slate-500"}>{t(c.status === "error" ? "var.error" : "var.rendering")}</span>}
        </div>
      </div>
    </div>
  );
};

export const VariantsPanel = ({ video, range, cfg, active }) => {
  const { t } = useI18n();
  const nav = useNavigate();
  const key = `clipx_variants_${video.id}`;
  const [gid, setGid] = useState(() => localStorage.getItem(key));
  const [clips, setClips] = useState([]);
  const [busy, setBusy] = useState(false);
  const [cmp, setCmp] = useState(false);
  useEffect(() => {
    if (!gid) return undefined;
    let stop = false;
    const tick = async () => {
      const { data } = await api.get(`/clips/variants/${gid}`).catch(() => ({ data: [] }));
      if (stop) return;
      setClips(data);
      if (data.some((c) => c.status === "rendering")) setTimeout(tick, 4000);
    };
    tick();
    return () => { stop = true; };
  }, [gid]);
  const create = async () => {
    if (range.end - range.start < 0.5) { toast.error(t("var.need_range")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/clips/variants", makeClipBody(video, range, cfg, active));
      localStorage.setItem(key, data.group);
      setClips(data.clips);
      setGid(data.group);
      toast.success(t("var.started"));
    } catch (e) {
      toast.error(formatApiError(e), e?.response?.status === 402 ? { action: { label: t("cr.get"), onClick: () => nav("/credits") }, duration: 8000 } : undefined);
    } finally { setBusy(false); }
  };
  const bestId = clips.reduce((b, c) => ((c.viral?.score ?? 0) > (b?.viral?.score ?? -1) ? c : b), null)?.id;
  const onKept = (id) => setClips((cs) => cs.filter((c) => c.id === id));
  return (
    <div className="cx-card rounded-xl p-4 sm:p-5 space-y-3" data-testid="variants-panel">
      <div className="flex items-center gap-2 text-white font-semibold"><Layers3 className="w-4 h-4 text-[#ff5a36]" />{t("var.title")}</div>
      <p className="text-[11px] text-slate-400">{t("var.desc")}</p>
      <button onClick={create} disabled={busy} data-testid="variants-create-btn" className="cx-btn w-full rounded-full py-3 text-sm font-semibold flex items-center justify-center gap-2">
        {busy ? <><Loader2 className="w-4 h-4 animate-spin" />{t("var.busy")}</> : <><Layers3 className="w-4 h-4" />{t("var.btn")}</>}
      </button>
      {clips.length > 0 && (
        <div className="space-y-2" data-testid="variants-list">
          {clips.length > 1 && (
            <button onClick={() => setCmp(true)} disabled={!clips.some((c) => c.status === "done")} data-testid="variants-compare-btn"
              className="w-full rounded-full py-2.5 text-xs font-semibold flex items-center justify-center gap-2 border border-[#3ad7ff]/40 text-[#3ad7ff] hover:bg-[#3ad7ff]/10 transition-colors disabled:opacity-40">
              <Maximize2 className="w-3.5 h-3.5" />{t("cmp.open")}
            </button>
          )}
          {clips.map((c) => <VariantCard key={c.id} c={c} best={clips.length > 1 && c.id === bestId} />)}
          <button onClick={() => nav("/clips")} data-testid="variants-see-all-btn" className="cx-ghost w-full rounded-full py-2 text-xs">{t("var.see_all")}</button>
        </div>
      )}
      <VariantsCompare open={cmp} onOpenChange={setCmp} gid={gid} clips={clips} bestId={bestId} onKept={onKept} />
    </div>
  );
};
